'use client'

import React from 'react'
import { Phone, MessageSquare, Image as ImageIcon, Link as LinkIcon, HelpCircle } from 'lucide-react'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses, compactDate } from '../ProcessedEntry/DomainEntry/processedTypes'
import type { Catalog } from './entryTypes'
import { findCatalogMatchByName } from './entryTypes'

// A from-scratch Tailwind rebuild of webapp's AltBentoGridItem — "how will this
// entry look once it's live in the public feed" — for the Casos side panel.
export type FeedPreviewEntry = {
  post_type?: string
  headline?: string
  title?: string
  message_content?: string
  message_subject?: string
  sender?: string
  call_type_text?: string
  created_at?: string
  is_scam?: boolean | null
  platform_id?: number | string | null
  message_type_id?: number | string | null
  call_type_id?: number | string | null
  platform_text?: string
  message_type_text?: string
  processed_tags?: string[]
  scam_claims?: string[]
}

function replaceSixthAndNinth(phone?: string) {
  if (!phone || phone.length < 9) return phone
  const chars = phone.split('')
  chars[5] = '*'
  chars[8] = '*'
  return chars.join('')
}

const POST_TYPE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  call: Phone,
  text: MessageSquare,
  image: ImageIcon,
  url: LinkIcon,
}

function StatusBadge({ isScam, theme }: { isScam?: boolean | null; theme: ProcessedTheme }) {
  const label = isScam === true ? 'Burla' : isScam === false ? 'Legítima' : 'Em análise'
  const cls =
    isScam === true
      ? 'border-destructive/40 bg-danger-surface text-destructive'
      : isScam === false
      ? 'border-success/40 bg-success-surface text-success'
      : 'border-warning-border bg-warning-surface text-warning'
  const dot = isScam === true ? 'bg-destructive' : isScam === false ? 'bg-success' : 'bg-warning'
  return (
    <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-[4px] border px-2 py-0.5 text-[10px] font-semibold ${cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  )
}

function Pill({ label, logoUrl, tone, theme }: { label: string; logoUrl?: string | null; tone?: 'danger'; theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  const cls =
    tone === 'danger'
      ? 'border-destructive/40 bg-danger-surface text-destructive'
      : `${c.line} ${c.soft} ${c.muted}`
  return (
    <span className={`inline-flex items-center gap-1 rounded-[4px] border px-2 py-0.5 text-[10px] ${cls}`}>
      {logoUrl && <img src={logoUrl} alt="" className="h-3 w-3 rounded-full object-contain" />}
      {label}
    </span>
  )
}

export function FeedPreviewCard({ entry, catalog, theme }: { entry: FeedPreviewEntry; catalog: Catalog; theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  const postType = entry.post_type ?? 'text'
  const isCall = postType === 'call'

  // Fall back to matching the free-text platform/type value against the
  // catalog by name when there's no resolved *_id, so entries the ingest
  // pipeline never linked can still show a logo.
  const platform =
    entry.platform_id !== undefined && entry.platform_id !== null
      ? catalog.platforms.find((p) => String(p.id) === String(entry.platform_id))
      : findCatalogMatchByName(catalog.platforms, entry.platform_text)
  const platformLabel = platform?.name ?? entry.platform_text
  const messageType =
    entry.message_type_id !== undefined && entry.message_type_id !== null
      ? catalog.messageTypes.find((m) => String(m.id) === String(entry.message_type_id))
      : findCatalogMatchByName(catalog.messageTypes, entry.message_type_text)
  const callType =
    entry.call_type_id !== undefined && entry.call_type_id !== null
      ? catalog.callTypes.find((ct) => String(ct.id) === String(entry.call_type_id))
      : findCatalogMatchByName(catalog.callTypes, entry.call_type_text)
  const callTypeLabel = callType?.name ?? entry.call_type_text
  const isDangerCallType = callType ? String(callType.id) === '1' : false

  const typeLabel = (isCall ? 'CHAMADA' : messageType?.name ?? platformLabel ?? postType).toUpperCase()
  const iconLogo = isCall ? undefined : messageType?.logo_url ?? platform?.logo_url
  const TypeIcon = POST_TYPE_ICON[postType] ?? HelpCircle

  const title = entry.headline ?? entry.title
  const tags = entry.processed_tags ?? []
  const claims = entry.scam_claims ?? []

  return (
    <div className={`border p-4 shadow-sm ${c.panel} ${c.line}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {iconLogo ? (
            <img src={iconLogo} alt="" className="h-7 w-7 shrink-0 rounded-full object-contain" />
          ) : (
            <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${c.soft}`}>
              <TypeIcon className={`h-3.5 w-3.5 ${c.faint}`} />
            </span>
          )}
          <span className={`truncate font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>{typeLabel}</span>
        </div>
        <StatusBadge isScam={entry.is_scam} theme={theme} />
      </div>

      {title && <div className={`mb-2 font-serif text-[15px] leading-snug ${c.text}`}>{title}</div>}

      {(platformLabel || (isCall && callTypeLabel) || tags.length > 0) && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {platformLabel && <Pill label={platformLabel} logoUrl={platform?.logo_url} theme={theme} />}
          {isCall && callTypeLabel && <Pill label={callTypeLabel} tone={isDangerCallType ? 'danger' : undefined} theme={theme} />}
          {tags.map((tag) => <Pill key={tag} label={tag} theme={theme} />)}
        </div>
      )}

      {isCall ? (
        <div>
          <div className={`font-serif text-[13px] ${c.text}`}>{replaceSixthAndNinth(entry.sender)}</div>
          {callTypeLabel && <div className={`mt-0.5 text-[11px] ${c.faint}`}>{callTypeLabel}</div>}
        </div>
      ) : (
        <div>
          {entry.sender && <div className={`mb-1 font-serif text-[13px] ${c.text}`}>{entry.sender}</div>}
          {entry.message_subject && <div className={`mb-0.5 text-[12px] font-semibold ${c.text}`}>{entry.message_subject}</div>}
          {entry.message_content && <div className={`line-clamp-4 text-[12px] leading-snug ${c.muted}`}>{entry.message_content}</div>}
        </div>
      )}

      {claims.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {claims.map((claim, i) => <Pill key={i} label={claim} tone="danger" theme={theme} />)}
        </div>
      )}

      {entry.created_at && (
        <div className={`mt-3 border-t pt-2 text-[10px] ${c.line} ${c.faint}`}>Apareceu {compactDate(entry.created_at)}</div>
      )}
    </div>
  )
}
